import { useState } from "react";
import { usePricing } from "../context/PricingContext";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { 
  ArrowLeft, Car, Baby, Dog, Briefcase, Star, 
  Shield, Phone, Smartphone, Bell, CheckCircle2, ChevronRight
} from "lucide-react";
import { protectionTypes, benefits } from "../lib/designTokens";
import { useI18n } from "../i18n";
import { parseApiError } from "../utils/apiError";

const API = '/api';

// Icon mapping
const iconMap = { Car, Baby, Dog, Briefcase, Star };

const NewProtection = () => {
  const { priceLabelSemantic, chargeNote, planMonths } = usePricing();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = select type, 2 = form
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [phoneErrors, setPhoneErrors] = useState({});

  // Validación internacional genérica (el teléfono no participa del flujo anónimo)
  const validateIntlContactPhone = (phone) => {
    if (!phone) return { valid: true };
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 15) {
      return { valid: false, message: t("owner.nprot.phone_invalid") };
    }
    return { valid: true, formatted: phone.trim().startsWith('+') ? `+${digits}` : digits };
  };

  const handleTypeSelect = (typeId) => {
    setSelectedType(typeId);
    setStep(2);
    // Initialize form data with empty values for all fields
    const initialData = {};
    protectionTypes[typeId].fields.forEach(field => {
      initialData[field.name] = '';
    });
    setFormData(initialData);
  };

  const handleInputChange = (fieldName, value) => {
    setFormData({ ...formData, [fieldName]: value });
    // Clear phone error when typing
    if (fieldName.includes('phone')) {
      setPhoneErrors({ ...phoneErrors, [fieldName]: null });
    }
  };

  const handlePhoneBlur = (fieldName) => {
    if (formData[fieldName]) {
      const validation = validateIntlContactPhone(formData[fieldName]);
      if (!validation.valid) {
        setPhoneErrors({ ...phoneErrors, [fieldName]: validation.message });
      } else {
        setPhoneErrors({ ...phoneErrors, [fieldName]: null });
        setFormData({ ...formData, [fieldName]: validation.formatted });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all phone fields
    const config = protectionTypes[selectedType];
    let hasErrors = false;
    const newErrors = {};
    
    config.fields.forEach(field => {
      if (field.type === 'tel' && formData[field.name]) {
        const validation = validateIntlContactPhone(formData[field.name]);
        if (!validation.valid) {
          newErrors[field.name] = validation.message;
          hasErrors = true;
        }
      }
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = t("owner.nprot.field_required");
        hasErrors = true;
      }
    });
    
    if (hasErrors) {
      setPhoneErrors(newErrors);
      toast.error(t("owner.nprot.fix_errors"));
      return;
    }
    
    setLoading(true);
    
    try {
      // RC-WEB-6 PHASE 3 — empty optional inputs must never reach the API as "".
      // `age: ""` made Pydantic answer a 422 whose `detail` array then crashed
      // React ("Objects are not valid as a React child").
      const cleaned = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value === "" || value === null || value === undefined) return;
        const fieldDef = config.fields.find((f) => f.name === key);
        cleaned[key] = fieldDef?.type === "number" ? Number(value) : value;
      });

      const payload = {
        ...cleaned,
        name: cleaned.name || cleaned.object_type || t("owner.nprot.default_name"),
        protection_type: selectedType,
      };

      const response = await axios.post(`${API}/vehicles`, payload);

      toast.success(t("owner.nprot.created"), {
        description: t("owner.nprot.created_desc")
      });

      // PHASE 4/5 — ONE canonical checkout, with the profile from the response.
      navigate(`/checkout/${response.data.id}`);

    } catch (error) {
      const parsed = parseApiError(error);
      if (parsed.fields && Object.keys(parsed.fields).length) {
        setPhoneErrors((prev) => ({ ...prev, ...parsed.fields }));
      }
      toast.error(parsed.message || t("owner.nprot.create_error"));
    } finally {
      setLoading(false);
    }
  };

  const config = selectedType ? protectionTypes[selectedType] : null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link 
              to="/dashboard" 
              className="flex items-center gap-2 text-slate-500 hover:text-[#0B2545] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>{t("owner.nprot.back")}</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Step 1: Type Selection */}
        {step === 1 && (
          <div className="animate-fade-in">
            <div className="text-center mb-10">
              <h1 className="text-3xl sm:text-4xl font-bold text-[#0B2545] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t("owner.nprot.what_protect")}
              </h1>
              <p className="text-slate-500 text-lg">
                {t("owner.nprot.select_type")}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.values(protectionTypes).map((type) => {
                const IconComponent = iconMap[type.icon];
                return (
                  <button
                    key={type.id}
                    onClick={() => handleTypeSelect(type.id)}
                    className="bg-white shadow-[0_8px_30px_rgba(11,37,69,0.05)] rounded-2xl border border-slate-200 p-6 text-left transition-all hover:bg-slate-50 hover:border-slate-300 hover:scale-[1.02] group"
                    data-testid={`select-type-${type.id}`}
                  >
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center mb-4 shadow-lg`}>
                      <span className="text-3xl">{type.emoji}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-[#0B2545] mb-2 flex items-center gap-2">
                      {t(`owner.ptype.${type.id}.label`)}
                      <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-[#0B2545] group-hover:translate-x-1 transition-all" />
                    </h3>
                    <p className="text-slate-500 text-sm">
                      {t(`owner.ptype.${type.id}.desc`)}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Dynamic Form */}
        {step === 2 && config && (
          <div className="grid lg:grid-cols-2 gap-8 animate-fade-in">
            {/* Left: Form */}
            <div className="order-2 lg:order-1">
              <button 
                onClick={() => setStep(1)}
                className="flex items-center gap-2 text-slate-500 hover:text-[#0B2545] transition-colors mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("owner.nprot.change_type")}
              </button>

              <div className="bg-white shadow-[0_8px_30px_rgba(11,37,69,0.05)] rounded-2xl border border-slate-200 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center`}>
                    <span className="text-2xl">{config.emoji}</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#0B2545]">
                      {t("owner.nprot.register")} {t(`owner.ptype.${config.id}.label`)}
                    </h2>
                    <p className="text-slate-500 text-sm">
                      {t("owner.nprot.complete_data")}
                    </p>
                  </div>
                </div>

                {config.heroMessage && (
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 mb-6">
                    <p className="text-emerald-400 text-sm">
                      💚 {t(`owner.ptype.${config.id}.hint`) === `owner.ptype.${config.id}.hint` ? config.heroMessage : t(`owner.ptype.${config.id}.hint`)}
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {config.fields.map((field) => (
                    <div key={field.name} className="space-y-2">
                      <Label className="text-slate-600 text-sm font-medium">
                        {t(`owner.ptype.${config.id}.f_${field.name}`) === `owner.ptype.${config.id}.f_${field.name}` ? field.label : t(`owner.ptype.${config.id}.f_${field.name}`)}
                        {field.required && <span className="text-red-400 ml-1">*</span>}
                      </Label>
                      
                      {field.type === 'select' ? (
                        <select
                          value={formData[field.name] || ''}
                          onChange={(e) => handleInputChange(field.name, e.target.value)}
                          required={field.required}
                          className="w-full h-12 bg-white border border-slate-200 text-[#0B2545] rounded-xl px-4 focus:border-[#1B84FF] focus:ring-1 focus:ring-[#1B84FF]/20 outline-none"
                          data-testid={`input-${field.name}`}
                        >
                          <option value="" className="bg-slate-800">{t("owner.nprot.select_placeholder")}</option>
                          {field.options?.map(opt => (
                            <option key={opt} value={opt} className="bg-slate-800">{t(`owner.ptype_opts.${opt}`) === `owner.ptype_opts.${opt}` ? opt : t(`owner.ptype_opts.${opt}`)}</option>
                          ))}
                        </select>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          value={formData[field.name] || ''}
                          onChange={(e) => handleInputChange(field.name, e.target.value)}
                          placeholder={t(`owner.ptype.${config.id}.ph_${field.name}`) === `owner.ptype.${config.id}.ph_${field.name}` ? field.placeholder : t(`owner.ptype.${config.id}.ph_${field.name}`)}
                          required={field.required}
                          rows={3}
                          className="w-full bg-white border border-slate-200 text-[#0B2545] rounded-xl p-4 focus:border-[#1B84FF] focus:ring-1 focus:ring-[#1B84FF]/20 outline-none placeholder:text-slate-400 resize-none"
                          data-testid={`input-${field.name}`}
                        />
                      ) : field.type === 'tel' ? (
                        <div>
                          <Input
                            type="tel"
                            value={formData[field.name] || ''}
                            onChange={(e) => handleInputChange(field.name, e.target.value)}
                            onBlur={() => handlePhoneBlur(field.name)}
                            placeholder={t(`owner.ptype.${config.id}.ph_${field.name}`) === `owner.ptype.${config.id}.ph_${field.name}` ? field.placeholder : t(`owner.ptype.${config.id}.ph_${field.name}`)}
                            required={field.required}
                            maxLength={16}
                            className={`h-12 bg-white border-slate-200 text-[#0B2545] placeholder:text-slate-400 focus:border-[#1B84FF] rounded-xl ${phoneErrors[field.name] ? 'border-red-500' : ''}`}
                            data-testid={`input-${field.name}`}
                          />
                          {phoneErrors[field.name] && (
                            <p className="text-red-400 text-sm mt-1">{phoneErrors[field.name]}</p>
                          )}
                        </div>
                      ) : (
                        <div>
                          <Input
                            type={field.type}
                            value={formData[field.name] || ''}
                            onChange={(e) => handleInputChange(field.name, e.target.value)}
                            placeholder={t(`owner.ptype.${config.id}.ph_${field.name}`) === `owner.ptype.${config.id}.ph_${field.name}` ? field.placeholder : t(`owner.ptype.${config.id}.ph_${field.name}`)}
                            required={field.required}
                            className={`h-12 bg-white border-slate-200 text-[#0B2545] placeholder:text-slate-400 focus:border-[#1B84FF] rounded-xl ${phoneErrors[field.name] ? 'border-red-500' : ''}`}
                            data-testid={`input-${field.name}`}
                          />
                          {phoneErrors[field.name] && (
                            <p className="text-red-400 text-sm mt-1">{phoneErrors[field.name]}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 mt-6 bg-[#1B84FF] hover:bg-[#0F6FDB] text-white font-semibold rounded-xl shadow-lg shadow-[#1B84FF]/30 transition-all hover:scale-[1.02] disabled:opacity-50"
                    data-testid="submit-protection"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t("owner.nprot.creating")}
                      </span>
                    ) : t("owner.nprot.continue_pay")}
                  </Button>
                </form>
              </div>
            </div>

            {/* Right: Benefits & Image */}
            <div className="order-1 lg:order-2">
              {/* Benefits */}
              <div className="bg-white shadow-[0_8px_30px_rgba(11,37,69,0.05)] rounded-2xl border border-slate-200 p-6 mb-6">
                <h3 className="text-lg font-semibold text-[#0B2545] mb-4">
                  {t("owner.nprot.includes")}
                </h3>
                <div className="space-y-3">
                  {[
                    { icon: Shield, text: t("owner.nprot.b1"), desc: t("owner.nprot.b1d") },
                    { icon: Phone, text: t("owner.nprot.b2"), desc: t("owner.nprot.b2d") },
                    { icon: Bell, text: t("owner.nprot.b3"), desc: t("owner.nprot.b3d") },
                    { icon: Smartphone, text: t("owner.nprot.b4"), desc: t("owner.nprot.b4d") }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#1B84FF]/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-[#1B84FF]" />
                      </div>
                      <div>
                        <p className="text-[#0B2545] text-sm font-medium">{item.text}</p>
                        <p className="text-slate-500 text-xs">{item.desc}</p>
                      </div>
                      <CheckCircle2 className="w-4 h-4 text-[#1B84FF] ml-auto" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div className="bg-gradient-to-br from-[#1B84FF]/20 to-[#1B84FF]/5 rounded-2xl border border-[#1B84FF]/30 p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">{t("owner.nprot.full_membership")}</p>
                    <p className="text-[#0B2545] text-3xl font-bold" data-testid="newprot-price">{priceLabelSemantic(planMonths)}</p>
                    {chargeNote(planMonths) && (
                      <p className="text-slate-500 text-xs mt-1" data-testid="newprot-charge-note">{chargeNote(planMonths)}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-[#1B84FF] font-semibold">{planMonths === 12 ? t("owner.dash.plan_1y") : t("owner.dash.plan_6m")}</p>
                    <p className="text-slate-500 text-sm">{t("owner.nprot.of_protection")}</p>
                  </div>
                </div>
              </div>

              {/* Hero Image */}
              <div className="relative">
                <img 
                  src={config.heroImage}
                  alt={`${config.label} protección`}
                  className="w-full rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
            </div>
          </div>
        )}
      </main>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default NewProtection;
